import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import {
  IShareTarget,
  ISharedMessageType,
} from '../../../dto/MessageSharing.dto';
import ShareManagementDialog from './ShareManagementDialog';
import { useDistrictMessageSharingAccess } from './useDistrictMessageSharingAccess';

interface DistrictShareActionProps {
  companyId: number;
  messageType: ISharedMessageType;
  requestId: number;
  existingTargets?: IShareTarget[];
  disabled?: boolean;
  label?: string;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
}

const DistrictShareAction: React.FC<DistrictShareActionProps> = ({
  companyId,
  messageType,
  requestId,
  existingTargets = [],
  disabled = false,
  label = 'Manage Sharing',
  variant = 'contained',
  size = 'small',
}) => {
  const {canManageDistrictSharing} = useDistrictMessageSharingAccess(companyId);
  const existingTargetsKey = useMemo(
    () =>
      JSON.stringify(
        existingTargets.map(target => ({
          target_type: target.target_type,
          target_company_id: target.target_company_id,
          target_station_id: target.target_station_id ?? null,
          target_company_school_id: target.target_company_school_id ?? null,
          starts_at: target.starts_at,
          ends_at: target.ends_at,
        })),
      ),
    [existingTargets],
  );
  const normalizedExistingTargets = useMemo(
    () => JSON.parse(existingTargetsKey) as IShareTarget[],
    [existingTargetsKey],
  );
  const [open, setOpen] = useState(false);
  const [currentTargets, setCurrentTargets] = useState(
    normalizedExistingTargets,
  );

  useEffect(() => {
    setCurrentTargets(normalizedExistingTargets);
  }, [normalizedExistingTargets]);

  if (!canManageDistrictSharing) {
    return null;
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        disabled={disabled}
        onClick={() => setOpen(true)}>
        {label}
      </Button>
      <ShareManagementDialog
        open={open}
        handleClose={() => setOpen(false)}
        messageType={messageType}
        requestId={requestId}
        companyId={companyId}
        existingTargets={currentTargets}
        onTargetsChange={setCurrentTargets}
      />
    </>
  );
};

export default DistrictShareAction;
